import React from 'react';
import { Page, Content } from '@backstage/core-components';
import { Box, makeStyles } from '@material-ui/core';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
// eslint-disable-next-line @backstage/no-forbidden-package-imports
import { evaluateEntity } from '@internal/backstage-plugin-maturity/src/utils/maturityEvaluator';
// eslint-disable-next-line @backstage/no-forbidden-package-imports
import { rules } from '@internal/backstage-plugin-maturity/src/rules/rules';
// eslint-disable-next-line @backstage/no-forbidden-package-imports
import { projOnboardApiRef } from '@internal/backstage-plugin-proj-onboard/src/api';
// eslint-disable-next-line @backstage/no-forbidden-package-imports
import type {
  ProjectSummary,
  ManualPhaseKey,
} from '@internal/backstage-plugin-proj-onboard/src/api';

import { useAllowedSystemsEntityFilter } from '../../hooks/useAllowedSystemsEntityFilter';
import {
  PlatformSnapshotCard,
  CatalogSummaryCard,
  AIAgenticCard,
  QuickLinksCard,
  StarredEntitiesCard,
  EntityKind,
} from './components';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
  },
  content: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: theme.spacing(2),
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
}));

type ProjectWithStats = ProjectSummary & {
  stats: { done: number; inProgress: number; todo: number };
  client?: string;
};

export const ExternalHomePage = () => {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const catalogApi = useApi(catalogApiRef);
  const projOnboardApi = useApi(projOnboardApiRef);

  const { allowedSystems } = useAllowedSystemsEntityFilter();

  // ── Uptime ──────────────────────────────────────────────────────────
  const { value: uptimeValue, loading: uptimeLoading } = useAsync(async () => {
    try {
      const { items: systems } = await catalogApi.getEntities({
        filter: { kind: 'System' },
      });
      const targetSystems = allowedSystems
        ? systems.filter(s =>
            allowedSystems.includes(s.metadata.name.toLowerCase()),
          )
        : systems;
      const limited = targetSystems.slice(0, 20);
      if (!limited.length) return null;

      const baseUrl = await discoveryApi.getBaseUrl('gatus-monitor');
      let totalChecks = 0;
      let successChecks = 0;

      await Promise.allSettled(
        limited.map(async system => {
          const ns = system.metadata.namespace ?? 'default';
          const name = system.metadata.name;
          const res = await fetchApi.fetch(`${baseUrl}/uptime/${ns}:${name}`, {
            method: 'GET',
          });
          if (!res.ok) return;
          const data = await res.json();
          const fulfilled = data
            .filter((d: any) => d.status === 'fulfilled')
            .map((d: any) => d.value);
          for (const endpoint of fulfilled) {
            for (const r of endpoint.results || []) {
              totalChecks++;
              if (r.success) successChecks++;
            }
          }
        }),
      );
      return totalChecks > 0
        ? Math.round((successChecks / totalChecks) * 100)
        : null;
    } catch {
      return null;
    }
  }, [allowedSystems]);

  // ── Maturity ────────────────────────────────────────────────────────
  const { value: maturityValue, loading: maturityLoading } =
    useAsync(async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Component' },
        });
        const scoped = allowedSystems
          ? items.filter(e => {
              const sys = (e.spec as any)?.system;
              if (!sys) return false;
              const sysName = String(sys).split('/').pop()?.toLowerCase();
              return allowedSystems.includes(sysName ?? '');
            })
          : items;
        if (!scoped.length) return null;

        let totalWeight = 0;
        let passedWeight = 0;
        for (const entity of scoped) {
          const result = evaluateEntity(entity, rules);
          totalWeight += result.totalWeight;
          passedWeight += result.passedWeight;
        }
        return totalWeight > 0
          ? (passedWeight / totalWeight) * 5 // Scale to 5.0 rating
          : null;
      } catch {
        return null;
      }
    }, [allowedSystems]);

  // ── Catalog summary data ────────────────────────────────────────────
  const { value: catalogData, loading: catalogLoading } = useAsync(async () => {
    try {
      const [servicesRes, apisRes, componentsRes] = await Promise.all([
        catalogApi.getEntities({
          filter: { kind: 'Component', 'spec.type': 'service' },
        }),
        catalogApi.getEntities({ filter: { kind: 'API' } }),
        catalogApi.getEntities({ filter: { kind: 'Component' } }),
      ]);

      // Filter by allowed systems if external
      const filterBySystem = (items: any[]) => {
        if (!allowedSystems) return items;
        return items.filter(e => {
          const sys = (e.spec as any)?.system;
          if (!sys) return false;
          const sysName = String(sys).split('/').pop()?.toLowerCase();
          return allowedSystems.includes(sysName ?? '');
        });
      };

      const services = filterBySystem(servicesRes.items);
      const apis = filterBySystem(apisRes.items);
      const components = filterBySystem(componentsRes.items);

      // Get recently updated entities
      const allEntities = [...services, ...apis, ...components];
      const sorted = allEntities
        .filter(
          e => e.metadata.annotations?.['backstage.io/managed-by-location'],
        )
        .sort((a, b) => {
          const aTime =
            a.metadata.annotations?.['backstage.io/last-updated'] || '';
          const bTime =
            b.metadata.annotations?.['backstage.io/last-updated'] || '';
          return bTime.localeCompare(aTime);
        })
        .slice(0, 5);

      return {
        servicesCount: services.length,
        apisCount: apis.length,
        componentsCount: components.length,
        recentEntities: sorted.map(e => ({
          kind: e.kind as EntityKind,
          name: e.metadata.name,
          namespace: e.metadata.namespace,
          owner:
            (e.spec as any)?.owner?.replace(/^(user:|group:)/, '') || undefined,
          updatedAt: e.metadata.annotations?.['backstage.io/last-updated'],
        })),
      };
    } catch {
      return {
        servicesCount: 0,
        apisCount: 0,
        componentsCount: 0,
        recentEntities: [],
      };
    }
  }, [allowedSystems]);

  // ── Projects data ───────────────────────────────────────────────────
  const { value: projectsData, loading: projectsLoading } =
    useAsync(async () => {
      try {
        const allProjects = await projOnboardApi.listProjects();
        const sorted = allProjects
          .sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          )
          .slice(0, 5);

        const enriched: ProjectWithStats[] = await Promise.all(
          sorted.map(async project => {
            let stats = { done: 0, inProgress: 0, todo: 0 };
            try {
              const phasesRes = await projOnboardApi.getManualTaskPhases(
                project.id,
              );
              const phases = Object.values(
                phasesRes.phases ?? {},
              ) as ManualPhaseKey[];
              for (const phase of phases) {
                if (phase === 'done' || phase === 'deployed') stats.done++;
                else if (
                  phase === 'inProgress' ||
                  phase === 'inTesting' ||
                  phase === 'doneDev'
                )
                  stats.inProgress++;
                else if (phase === 'todo' || phase === 'backlog') stats.todo++;
              }
            } catch {
              /* keep stats at 0 */
            }
            return { ...project, stats };
          }),
        );

        // Calculate total stats
        const totalStats = enriched.reduce(
          (acc, p) => ({
            done: acc.done + p.stats.done,
            inProgress: acc.inProgress + p.stats.inProgress,
            todo: acc.todo + p.stats.todo,
          }),
          { done: 0, inProgress: 0, todo: 0 },
        );

        return { projects: enriched, totalStats };
      } catch {
        return {
          projects: [],
          totalStats: { done: 0, inProgress: 0, todo: 0 },
        };
      }
    }, []);

  return (
    <Page themeId="home">
      <Content noPadding>
        <Box className={classes.root}>
          <Box className={classes.content}>
            <Box className={classes.layout}>
              {/* Main column */}
              <Box className={classes.mainColumn}>
                <PlatformSnapshotCard
                  uptime={uptimeValue}
                  uptimeLoading={uptimeLoading}
                  maturity={maturityValue}
                  maturityLoading={maturityLoading}
                  showMembers={false}
                />

                <CatalogSummaryCard
                  servicesCount={catalogData?.servicesCount}
                  apisCount={catalogData?.apisCount}
                  componentsCount={catalogData?.componentsCount}
                  recentEntities={catalogData?.recentEntities}
                  loading={catalogLoading}
                />

                <AIAgenticCard
                  projects={projectsData?.projects}
                  totalStats={projectsData?.totalStats}
                  loading={projectsLoading}
                />
              </Box>

              {/* Side column */}
              <Box className={classes.sideColumn}>
                <QuickLinksCard isInternal={false} />
                <StarredEntitiesCard />
              </Box>
            </Box>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
